import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { squidloansUserAPI } from '../../services/api';
import { automationNodeLeadStageToRouteMap, LEAD_STAGE } from '../../Utils/constants';
import useLeadStage from '../../hooks/useLeadStage';
import FlowFooter from '../../components/FlowFooter';

function PreEsignAutomation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ekycStatus, setEkycStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'warning' });
  const { leadStage, isLoadingStage } = useLeadStage();
  const showToast = (message, type = 'warning') => {
    setToast({ show: true, message, type });
  };


  const curr_page_lead_stage = [
    LEAD_STAGE.LOAN_REQUESTED,
    LEAD_STAGE.SEND_AADHAAR_OTP
  ];

  useEffect(() => {
    if (isLoadingStage) {
      return;
    }

    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadStage, navigate, isLoadingStage]);

  useEffect(() => {
    // Only proceed with E-KYC check after journey is loaded
    if (isLoadingStage) {
      return;
    }

    // Only check E-KYC if user is on valid stage
    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      return; // Will be handled by routing useEffect
    }

    const fetchDigiLockerEkycDetails = async () => {
      try {
        setLoading(true);
        const response = await squidloansUserAPI.submitAadharOTP();

        // If we get a successful response, proceed to E-sign
        if (response.success) {
          navigate("/apply/kyc-documents");
        }
        else if (response.message === "Your KYC is already approved") {
          navigate("/apply/kyc-documents");
        }
        else if (response.message.includes("KYC Mismatch")) {
          navigate("/apply/thank-you");
        }
        else {
          setEkycStatus("pending");
        }
      } catch (err) {
        if (err.message === "Your KYC is already approved") {
          navigate("/apply/kyc-documents");
          return;
        }
        if (err.message.includes("Your Lead is rejected")) {
          navigate("/apply/thank-you");
          return;
        }
        if (err.message?.includes("KYC initiation not found")) {
          setEkycStatus("pending");
          showToast(err.message || "Please initiate KYC first.", "error");
          return;
        }
        setEkycStatus("pending");
      } finally {
        setLoading(false);
      }
    };

    fetchDigiLockerEkycDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isLoadingStage, leadStage]);

  const handleCompleteEkyc = async () => {
    try {
      setIsLoading(true);
      const response = await squidloansUserAPI.initiateKYC({});
      if (response.success && response.redirectUrl) {
        window.location.href = response.redirectUrl;
      } else {
        const errorMessage =
          response.message || 'Failed to initiate KYC. Please try again.';
        showToast(errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage =
        error.message || 'An error occurred while initiating KYC. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
        <div className="relative flex-1 flex items-center justify-center px-4 pt-20 sm:pt-24 md:pt-28">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-xl">
            <h2 className="text-base sm:text-lg font-semibold mb-2 text-[#1E3A8A]">
              Verifying your <span className="text-[#FF6B35]">E-KYC</span>
            </h2>
            <p className="text-xs sm:text-sm text-[#64748B] mb-4">
              Please wait while we process your verification...
            </p>
            <div className="h-10 w-10 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin mx-auto" />
          </div>
        </div>
        <FlowFooter />
      </div>
    );
  }

  if (ekycStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
          <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
          <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
        </div>

        <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
          <div className="max-w-md w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
              <div className="text-center mb-4 sm:mb-6">
                <h2 className="text-base sm:text-lg font-medium mb-2 text-[#1E3A8A]">
                  Your <span className="text-[#FF6B35]">E-KYC</span> is pending
                </h2>
                <p className="text-xs sm:text-sm text-[#64748B]">
                  Please complete your E-KYC verification to proceed.
                </p>
              </div>

              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 text-xs sm:text-sm text-[#64748B]">
                <p className="font-semibold text-[#1E293B] mb-1">
                  Important information
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>E-KYC is required to proceed with your loan application.</li>
                  <li>The process is secure and government-approved.</li>
                  <li>You will be redirected to the verification partner.</li>
                </ul>
              </div>

              <button
                onClick={handleCompleteEkyc}
                disabled={isLoading}
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Complete E-KYC'
                )}
              </button>

              {toast.show && (
                <p className="text-xs text-red-500 mt-3">{toast.message}</p>
              )}
            </div>
          </div>
        </div>
        <FlowFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="relative flex-1 flex items-center justify-center px-4 pt-20 sm:pt-24 md:pt-28">
        <p className="text-sm sm:text-base text-[#64748B]">Loading...</p>
      </div>
      <FlowFooter />
    </div>
  );
}

export default PreEsignAutomation;

